import React, { useEffect, useState } from "react";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { db, auth } from "../firebaseConfig";
import { useLocation, Link } from "react-router-dom";
import MessageForm from "../components/MessageForm";
import User from "../components/User";
import Message from "../components/Message";

const Chat = () => {
  const user1 = auth.currentUser.uid;

  const [users, setUsers] = useState([]);
  const [chat, setChat] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [online, setOnline] = useState({});
  const [otherRating, setOtherRating] = useState(null);
  const [otherRatingCount, setOtherRatingCount] = useState(0);

  const location = useLocation();

  const fetchUserRating = async (userId) => {
    try {
      const ratingsRef = collection(db, "users", userId, "ratings");
      const snap = await getDocs(ratingsRef);
      if (!snap.empty) {
        let sum = 0;
        snap.forEach((doc) => (sum += doc.data().rating));
        setOtherRating(sum / snap.size);
        setOtherRatingCount(snap.size);
      } else {
        setOtherRating(null);
        setOtherRatingCount(0);
      }
    } catch (err) {
      console.error("Error fetching user rating:", err);
      setOtherRating(null);
      setOtherRatingCount(0);
    }
  };

  const selectUser = async (userObj) => {
    setChat(userObj);
    if (userObj.other?.uid) {
      await fetchUserRating(userObj.other.uid);
    }
  };

  const getChatFromAd = async (ad) => {
    try {
      const buyerSnap = await getDoc(doc(db, "users", user1));
      const sellerSnap = await getDoc(doc(db, "users", ad.postedBy));
      if (!sellerSnap.exists()) {
        console.warn("Organizer user does not exist (deleted?).");
        return;
      }
      const data = {
        ad,
        me: buyerSnap.exists() ? buyerSnap.data() : {},
        other: sellerSnap.data(),
      };
      setChat(data);
      await fetchUserRating(sellerSnap.id);
    } catch (err) {
      console.error("Error in getChatFromAd:", err);
    }
  };

  const getList = async () => {
    try {
      const msgRef = collection(db, "messages");
      const qMsg = query(msgRef, where("users", "array-contains", user1));
      const msgsSnap = await getDocs(qMsg);
      const messages = msgsSnap.docs.map((doc) => doc.data());

      let usersList = [];
      let unsubscribes = [];

      for (const message of messages) {
        try {
          const adRef = doc(db, "ads", message.ad);
          const meRef = doc(db, "users", user1);
          const otherRef = doc(
            db,
            "users",
            message.users.find((id) => id !== user1)
          );

          const [adDoc, meDoc, otherDoc] = await Promise.all([
            getDoc(adRef),
            getDoc(meRef),
            getDoc(otherRef),
          ]);

          if (!otherDoc.exists()) continue;
          if (!adDoc.exists()) continue;

          usersList.push({
            ad: adDoc.data(),
            me: meDoc.exists() ? meDoc.data() : {},
            other: otherDoc.data(),
          });

          const unsub = onSnapshot(otherRef, (snap) => {
            if (snap.exists()) {
              setOnline((prev) => ({
                ...prev,
                [snap.id]: snap.data().isOnline,
              }));
            }
          });
          unsubscribes.push(unsub);
        } catch (innerErr) {
          console.error("Error loading chat item:", innerErr);
        }
      }
      setUsers(usersList);

      return () => {
        unsubscribes.forEach((f) => f());
      };
    } catch (err) {
      console.error("Error in getList:", err);
    }
  };

  useEffect(() => {
    if (location.state?.ad) {
      getChatFromAd(location.state.ad);
    }
    getList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!chat) return;
    const user2 = chat.other?.uid;
    if (!user2) return;

    try {
      const chatId =
        user1 > user2
          ? `${user1}.${user2}.${chat.ad.adId}`
          : `${user2}.${user1}.${chat.ad.adId}`;

      const msgsRef = collection(db, "messages", chatId, "chat");
      const qMsgs = query(msgsRef, orderBy("createdAt", "asc"));

      const unsub = onSnapshot(qMsgs, (snapshot) => {
        let msgsArr = [];
        snapshot.forEach((doc) => msgsArr.push(doc.data()));
        setMsgs(msgsArr);
      });

      const docSnapRef = doc(db, "messages", chatId);
      getDoc(docSnapRef).then((docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.lastSender !== user1 && data.lastUnread) {
            updateDoc(docSnapRef, { lastUnread: false });
          }
        }
      });

      return () => unsub();
    } catch (err) {
      console.error("Error setting up messages subscription:", err);
    }
  }, [chat, user1]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!chat) return;

    const user2 = chat.other.uid;
    const chatId =
      user1 > user2
        ? `${user1}.${user2}.${chat.ad.adId}`
        : `${user2}.${user1}.${chat.ad.adId}`;

    try {
      const newMsgRef = doc(collection(db, "messages", chatId, "chat"));
      await setDoc(newMsgRef, {
        text,
        sender: user1,
        createdAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "messages", chatId), {
        lastText: text,
        lastSender: user1,
        lastUnread: true,
      });
      setText("");
    } catch (err) {
      console.error("Error sending message:", err);
    }
  };

  return (
    // Весь блок чата растягиваем и скрываем общий скролл
    <div className="row g-0 flex-fill" style={{ minHeight: 0, overflow: "hidden" }}>
      {/* Левая колонка (список чатов) */}
      <div
        className="col-sm-4 col-md-3 users_container"
        style={{ borderRight: "1px solid #ddd", overflowY: "auto" }}
      >
        {users.map((userObj, index) => (
          <User
            key={index}
            user={userObj}
            selectUser={selectUser}
            chat={chat}
            online={online}
            user1={user1}
          />
        ))}
      </div>

      {/* Правая колонка: flex-колонка, чтобы "шапки" не скроллились */}
      <div
        className="col-sm-8 col-md-9 d-flex flex-column"
        style={{ minHeight: 0, overflow: "hidden" }}
      >
        {chat ? (
          <>
            {/* Верхняя панель (фиксирована сверху) */}
            <div
              className="text-center py-2"
              style={{ borderBottom: "1px solid #ddd", flexShrink: 0 }}
            >
              <h3>
                {chat.other?.uid ? (
                  <Link to={`/profile/${chat.other.uid}`}>
                    {chat.other?.name || "Unknown User"}
                  </Link>
                ) : (
                  <span className="text-muted">Deleted User</span>
                )}
              </h3>
              {otherRatingCount > 0 ? (
                <p>
                  Rating: {otherRating?.toFixed(1)}/5 ({otherRatingCount}{" "}
                  {otherRatingCount === 1 ? "vote" : "votes"})
                </p>
              ) : (
                <p>No rating yet</p>
              )}
            </div>

            {/* Блок с инфой об объявлении (тоже не скроллится) */}
            <div
              className="p-2"
              style={{ borderBottom: "1px solid #ddd", flexShrink: 0 }}
            >
              <div className="d-flex align-items-center">
                {chat.ad?.images?.length ? (
                  <img
                    src={chat.ad.images[0].url}
                    alt={chat.ad.title}
                    style={{ width: "50px", height: "50px" }}
                  />
                ) : null}
                <div className="d-flex align-items-center justify-content-between flex-grow-1 ms-2">
                  <div>
                    <h6>{chat.ad?.title || "No Title"}</h6>
                    {chat.ad?.price && <small>Price: {chat.ad.price}</small>}
                  </div>
                  {chat.ad?.category && chat.ad?.adId && (
                    <Link
                      className="btn btn-secondary btn-sm"
                      to={`/${chat.ad.category.toLowerCase()}/${chat.ad.adId}`}
                    >
                      View Ad
                    </Link>
                  )}
                </div>
              </div>
            </div>

            {/* Список сообщений (прокручиваемая область) */}
            <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
              {msgs.map((msg, i) => (
                <Message key={i} msg={msg} user1={user1} />
              ))}
            </div>

            {/* Форма отправки (фиксирована снизу) */}
            <div style={{ borderTop: "1px solid #ddd", flexShrink: 0 }}>
              <MessageForm text={text} setText={setText} handleSubmit={handleSubmit} />
            </div>
          </>
        ) : (
          // Когда чат не выбран
          <div className="text-center mt-5 d-flex justify-content-center align-items-center flex-grow-1">
            <h3>Select a user to start conversation</h3>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
